// Single source of parent/student-facing class labels.
//
// Contract: docs/contracts/FEATURE_CONTRACT_PORTAL_CLASS_DISPLAY_2026_08.md
// § "Label composition rule". Every portal surface that names a class routes
// through this module. The historical leak (#414) was the teacher-update block
// carrying its own formatting, so the fix lives at the helper altitude rather
// than at each render site.
//
// Rule 1 is absolute: the raw internal class `name` (`Sat_PM_SD`,
// `Junior_Debate_Aug_2-4`, …) is an operator code and is NEVER an input here.
// It stays in the wire payload only as the feedback/homework matcher join key.
import { formatDate } from '../parent/ParentLayout.helpers'

// Word-boundary EN day names, full and abbreviated, case-insensitive.
// Longest-first so the alternation prefers the full name. Plural full forms
// ("Saturdays 9-11") count too — time_display is operator-editable free text.
const EN_WEEKDAY_TOKEN =
  /\b(?:mondays?|tuesdays?|wednesdays?|thursdays?|fridays?|saturdays?|sundays?|mon|tues|tue|weds|wed|thurs|thur|thu|fri|sat|sun)\b/i
// CN forms: 周X / 週X / 星期X / 礼拜X.
const CN_WEEKDAY_TOKEN = /(?:星期|礼拜|禮拜|周|週)[一二三四五六日天]/

// "Empty alias" is one definition everywhere: null, absent, '', and
// whitespace-only all take the same fallback chain.
function _clean(value) {
  return typeof value === 'string' ? value.trim() : ''
}

/**
 * Primary line for a class or session row.
 *
 * alias → effective time display → generic `t('classLabelFallback')`.
 * The time string is returned verbatim (never parsed, split, or rewritten):
 * `TOC_Nationals_PF`'s `1:00–3:00pm & 3:30–5:30pm` must survive untouched.
 */
export function classPrimaryLabel({ displayName, timeDisplay, t } = {}) {
  const alias = _clean(displayName)
  if (alias) return alias
  if (_clean(timeDisplay)) return timeDisplay
  return typeof t === 'function' ? t('classLabelFallback') : 'Class'
}

/**
 * Did the primary line fall back to the time display?
 *
 * Callers need this to satisfy the contract's "NULL alias ≡ status quo" rule —
 * the secondary line must not repeat a time that is already the primary line.
 * Derived from the same `_clean` as `classPrimaryLabel` so the two can never
 * disagree about what counts as an empty alias.
 */
export function classLabelUsesTime({ displayName, timeDisplay } = {}) {
  return !_clean(displayName) && Boolean(_clean(timeDisplay))
}

/** Does a time string already name a weekday? */
export function containsWeekdayToken(value) {
  const text = _clean(value)
  if (!text) return false
  return EN_WEEKDAY_TOKEN.test(text) || CN_WEEKDAY_TOKEN.test(text)
}

const _weekdayFormatters = new Map()

// `dateStr + 'T00:00:00'` is a LOCAL parse, matching ParentLayout.helpers.js.
// Appending nothing (or a `Z`) would parse as UTC and shift the weekday for
// every parent east of Greenwich — the same class of bug as #413.
function _validDateString(sessionDate) {
  const dateStr = _clean(sessionDate)
  if (!dateStr) return ''
  const parsed = new Date(`${dateStr}T00:00:00`)
  return Number.isNaN(parsed.getTime()) ? '' : dateStr
}

function _weekdayLabel(dateStr, language) {
  if (!dateStr) return ''
  const locale = language === 'cn' ? 'zh-CN' : 'en-US'
  try {
    let formatter = _weekdayFormatters.get(locale)
    if (!formatter) {
      formatter = new Intl.DateTimeFormat(locale, { weekday: 'short' })
      _weekdayFormatters.set(locale, formatter)
    }
    return formatter.format(new Date(`${dateStr}T00:00:00`))
  } catch {
    // A missing/limited Intl degrades to no weekday, never to a broken row.
    return ''
  }
}

/**
 * Secondary line for a session row: `[weekday] [time] · [date] · #N`.
 *
 * - Weekday follows the language toggle (EN `Thu`, CN `周四`).
 * - Weekday is SUPPRESSED when the time string already carries one, so a
 *   `Saturday 9:00 AM – 11:00 AM` class never renders `Sat Saturday …`.
 * - Time is OMITTED entirely when it was used as the primary line, which
 *   makes a NULL-alias class render exactly the pre-#414 layout.
 */
export function classSecondaryLine({
  sessionDate,
  timeDisplay,
  sessionNumber,
  language,
  usedTimeAsPrimary,
} = {}) {
  const dateStr = _validDateString(sessionDate)
  const segments = []

  if (!usedTimeAsPrimary) {
    const time = _clean(timeDisplay) ? timeDisplay : (language === 'cn' ? '时间暂不可用' : 'Time unavailable')
    const weekday = containsWeekdayToken(time) ? '' : _weekdayLabel(dateStr, language)
    const head = [weekday, time].filter(Boolean).join(' ')
    if (head) segments.push(head)
  }

  const dateLabel = formatDate(dateStr)
  if (dateLabel) segments.push(dateLabel)
  if (sessionNumber != null && sessionNumber !== '') segments.push(`#${sessionNumber}`)

  return segments.join(' · ')
}
